import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import Modal from "../components/Modal";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Save, FileText } from "lucide-react";

const CATEGORIES = ["Confirmación", "Recordatorio", "Reagendar", "No-show", "Reseña", "Recuperación", "Pago"];

const VARS = ["{patient_name}", "{appointment_date}", "{appointment_time}", "{doctor_name}", "{clinic_name}", "{service_name}", "{promo}", "{review_link}", "{payment_link}"];

const highlight = (s) => s.replace(/\{[a-z_]+\}/g, (m) => `‹${m}›`);

export default function Templates() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // template object or {} for new
  const [form, setForm] = useState({ name: "", category: "Confirmación", body: "" });

  const load = () => api.get("/templates").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ name: "", category: "Confirmación", body: "" });
    setEditing({ new: true });
  };
  const openEdit = (t) => {
    setForm({ id: t.id, name: t.name, category: t.category, body: t.body });
    setEditing(t);
  };

  const save = async () => {
    if (!form.name || !form.body) return toast.error("Nombre y mensaje son obligatorios");
    await api.post("/templates", form);
    toast.success("Plantilla guardada");
    setEditing(null);
    load();
  };

  const remove = async (t) => {
    await api.delete(`/templates/${t.id}`);
    toast.success("Plantilla eliminada");
    load();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-7xl mx-auto w-full">
      <PageHeader
        title="Plantillas de mensajes"
        subtitle="Textos guardados para copiar y pegar en tus conversaciones."
        actions={
          <button onClick={openNew} className="inline-flex items-center gap-2 bg-brand-electric text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-700 shadow-sm" data-testid="templates-new">
            <Plus className="w-4 h-4" /> Nueva plantilla
          </button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((t) => (
          <div key={t.id} className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden flex flex-col" data-testid={`template-card-${t.id}`}>
            <div className="px-5 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900">{t.name}</div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-blue-600 mt-0.5">{t.category}</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(t)} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100" data-testid={`template-edit-${t.id}`}><Edit3 className="w-4 h-4" /></button>
                <button onClick={() => remove(t)} className="p-1.5 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50" data-testid={`template-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-5 text-sm text-slate-700 whitespace-pre-line leading-relaxed flex-1">
              {highlight(t.body)}
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.new ? "Nueva plantilla" : "Editar plantilla"}
        size="lg"
        testid="modal-template"
        footer={
          <>
            <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50">Cancelar</button>
            <button onClick={save} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-brand-electric text-white hover:bg-blue-700" data-testid="template-save"><Save className="w-4 h-4" /> Guardar</button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Nombre</span>
              <input className="input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Categoría</span>
              <select className="input mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="template-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Cuerpo del mensaje</span>
            <textarea className="input mt-1" rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="template-body" />
          </label>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Variables disponibles</div>
            <div className="flex flex-wrap gap-1.5">
              {VARS.map((v) => (
                <button key={v} onClick={() => setForm((f) => ({ ...f, body: f.body + " " + v }))} className="text-[11px] font-mono bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded-md" data-testid={`var-${v}`}>{v}</button>
              ))}
            </div>
          </div>
        </div>
        <style>{`.input { width:100%; border:1px solid #cbd5e1; background:#fff; border-radius:8px; padding:.55rem .75rem; font-size:.875rem; outline:none; } .input:focus { border-color:#2563EB; box-shadow:0 0 0 2px rgba(37,99,235,.18); }`}</style>
      </Modal>
    </div>
  );
}
